// ABOUTME: React Query mutation hooks for creating, updating, and deleting order cancellation lead time configurations.
// ABOUTME: Transforms form values to API request shape and invalidates queries on success.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../../App.store'
import {
  OrderCancellationLeadTimeApiResponse,
  OrderCancellationLeadTimeFormValues,
  PutOrderCancellationLeadTimeRequest,
  OrderCancellationLeadTimeOverrides,
} from '../types/orderCancellationLeadTime'
import { toCreateScopeAndConfigRequest } from '../utils/pendingScope'
import type { PendingScopeRequest } from '../context/EntityFormContext'

const toApiRequest = (values: OrderCancellationLeadTimeFormValues, overrides: OrderCancellationLeadTimeOverrides): PutOrderCancellationLeadTimeRequest => ({
  farDistanceThreshold: { isOverridden: overrides.farDistanceThreshold, value: { threshold: values.farDistanceThreshold, uomId: values.farDistanceThresholdUomId } },
  leadTimes: {
    isOverridden: overrides.leadTimes,
    value: {
      standard: values.standard ? {
        applicableDays: values.standard.applicableDays,
        baseLeadTime: values.standard.baseLeadTime,
        baseLeadTimeUomId: values.standard.baseLeadTimeUomId,
        referenceEvent: values.standard.referenceEvent,
        farDistanceModifier: values.standard.farDistanceModifier,
        farDistanceModifierUomId: values.standard.farDistanceModifierUomId,
        calculationMethod: values.standard.calculationMethod,
      } : null!, // Unreachable — guarded by useOrderCancellationLeadTimeEntity.handleSave
      nonStandard: values.nonStandard.map((ns) => ({
        day: ns.day,
        baseLeadTime: ns.baseLeadTime,
        baseLeadTimeUomId: ns.baseLeadTimeUomId,
        referenceEvent: ns.referenceEvent,
        farDistanceModifier: ns.farDistanceModifier,
        farDistanceModifierUomId: ns.farDistanceModifierUomId,
        calculationMethod: ns.calculationMethod,
      })),
    },
  },
})

export const useCreateOrderCancellationLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: OrderCancellationLeadTimeFormValues; overrides: OrderCancellationLeadTimeOverrides }) => {
      const res = await api.post<OrderCancellationLeadTimeApiResponse>(`/tuc-configs/order-cancellation-lead-times/${scopeId}`, toApiRequest(values, overrides))
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderCancellationLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderCancellationLeadTime', countryId, businessLine] })
    },
  })
}

export const useCreateOrderCancellationLeadTimeWithScope = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ scope, values, overrides }: { scope: PendingScopeRequest; values: OrderCancellationLeadTimeFormValues; overrides: OrderCancellationLeadTimeOverrides }) => {
      const res = await api.post<OrderCancellationLeadTimeApiResponse>('/tuc-configs/order-cancellation-lead-times', toCreateScopeAndConfigRequest(scope, toApiRequest(values, overrides)))
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderCancellationLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderCancellationLeadTime', countryId, businessLine] })
    },
  })
}

export const useUpdateOrderCancellationLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ scopeId, values, overrides }: { scopeId: string; values: OrderCancellationLeadTimeFormValues; overrides: OrderCancellationLeadTimeOverrides }) => {
      const res = await api.put<OrderCancellationLeadTimeApiResponse>(`/tuc-configs/order-cancellation-lead-times/${scopeId}`, toApiRequest(values, overrides))
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderCancellationLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderCancellationLeadTime', countryId, businessLine] })
    },
  })
}

export const useDeleteOrderCancellationLeadTime = (countryId: string | undefined, businessLine: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scopeId: string) => { await api.delete(`/tuc-configs/order-cancellation-lead-times/${scopeId}`) },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderCancellationLeadTimeConfig', countryId, businessLine] })
      queryClient.invalidateQueries({ queryKey: ['navigatorScopes', 'orderCancellationLeadTime', countryId, businessLine] })
    },
  })
}
